import { Modal, Spin, Empty } from "antd";
import { skipToken } from "@reduxjs/toolkit/query";
import React from "react";
import { useGetOrderByIdQuery, CalcProducedPerProcess } from "shared/api";

interface ProductionDetailsModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number | null;
  type: "item" | "quantity" | null;
}

export const ProductionDetailsModal: React.FC<ProductionDetailsModalProps> = ({
  open,
  onClose,
  orderId,
  type,
}) => {
  const { data, isLoading } = useGetOrderByIdQuery(
    orderId ? { id: orderId, expand: "calc_produced" } : skipToken
  );

  const order = Array.isArray(data) ? data[0] : data;

  const calc = order?.calc_produced;

  /* ------------------------- LOADING ------------------------- */
  if (isLoading) {
    return (
      <Modal open={open} onCancel={onClose} footer={null} width={900}>
        <section className="py-16 text-center">
          <Spin size="large" />
        </section>
      </Modal>
    );
  }

  /* ------------------------- EMPTY DATA ------------------------- */
  if (!calc || !type) {
    return (
      <Modal open={open} onCancel={onClose} footer={null} width={900}>
        <section className="py-16">
          <Empty description="No production data available" />
        </section>
      </Modal>
    );
  }

  /* ------------------------- RENDER ------------------------- */

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={900}>
      <header className="mb-4">
        <h2 className="text-2xl font-semibold">
          {type === "item" ? "Item Progress" : "Quantity Progress"}
        </h2>
      </header>

      <section className="mb-6 text-[15px] space-y-1">
        <p>
          <b>Total Target:</b> {calc[`${type}_target`]}
        </p>
        <p>
          <b>Total Current:</b> {calc[`${type}_current`]}
        </p>
        <p>
          <b>Total %:</b>{" "}
          {calc[`${type}_percentage`] != null
            ? `${calc[`${type}_percentage`].toFixed(2)}%`
            : "—"}
        </p>
      </section>

      <hr className="my-4 border-gray-300" />

      <section>
        <h3 className="text-xl font-medium mb-4">Per Process</h3>

        {Object.values(calc.per_process).length === 0 && (
          <Empty description="No process data available" />
        )}

        <ul className="space-y-6">
          {Object.values(calc.per_process).map(
            (proc: CalcProducedPerProcess) => (
              <li key={proc.order_process_step_tracker_id}>
                <article className="p-5 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
                  <header>
                    <h4 className="text-lg font-semibold mb-2">
                      {proc.process_name}
                    </h4>
                  </header>

                  <p className="mb-3 text-[15px]">
                    <b>Target:</b> {proc[`${type}_target`]} &nbsp; | &nbsp;
                    <b>Current:</b> {proc[`${type}_current`]} &nbsp; | &nbsp;
                    <b>%:</b>{" "}
                    {proc[`${type}_percentage`] != null
                      ? `${proc[`${type}_percentage`].toFixed(2)}%`
                      : "—"}
                  </p>

                  <aside className="ml-3">
                    <h5 className="font-medium mb-2 text-[15px]">Materials</h5>

                    {Object.values(proc.per_material).length === 0 ? (
                      <Empty description="No materials" />
                    ) : (
                      <ul className="space-y-2">
                        {Object.values(proc.per_material).map((mat) => (
                          <li
                            key={mat.material_id}
                            className="p-3 bg-white border border-gray-200 rounded-md shadow-sm"
                          >
                            <b>{mat.material_name}</b>
                            <div className="mt-1 text-[14px]">
                              Current: {mat[`${type}_current`]} /{" "}
                              {mat[`${type}_target`]} —{" "}
                              {mat[`${type}_percentage`] != null
                                ? `${mat[`${type}_percentage`].toFixed(2)}%`
                                : "—"}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </aside>
                </article>
              </li>
            )
          )}
        </ul>
      </section>
    </Modal>
  );
};
